/**
 * Unified Response Format Helpers
 * These functions provide consistent handling of backend responses
 * across all API files
 */

/**
 * Extracts data from backend response
 * Handles both formats: { data: T } and T directly
 */
export function extractResponseData<T>(response: any): T {
  if (!response || !response.data) {
    throw new Error('INVALID_SERVER_RESPONSE'); // Will be translated in client
  }

  const responseData = response.data;

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 extractResponseData - Input:', {
      hasResponse: !!response,
      hasData: !!response?.data,
      responseDataType: typeof responseData,
      isArray: Array.isArray(responseData),
      hasNestedData:
        typeof responseData === 'object' && responseData !== null && 'data' in responseData,
      responseDataKeys:
        typeof responseData === 'object' && responseData !== null ? Object.keys(responseData) : [],
    });
  }

  // If response.data is an array, return it directly
  if (Array.isArray(responseData)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 extractResponseData - Returning array directly');
    }
    return responseData as T;
  }

  // If response.data has a 'data' property, extract it
  if (typeof responseData === 'object' && responseData !== null && 'data' in responseData) {
    const nestedData = (responseData as { data: T }).data;
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 extractResponseData - Extracting nested data:', {
        nestedDataType: typeof nestedData,
        nestedDataKeys:
          typeof nestedData === 'object' && nestedData !== null ? Object.keys(nestedData) : [],
      });
    }
    return nestedData;
  }

  // Otherwise, return response.data directly
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 extractResponseData - Returning responseData directly:', {
      responseDataType: typeof responseData,
      responseDataKeys:
        typeof responseData === 'object' && responseData !== null ? Object.keys(responseData) : [],
    });
  }
  return responseData as T;
}

/**
 * Extracts array data from backend response
 * Handles both formats: T[] and { data: T[] } and { data: T[], pagination: {...} }
 */
export function extractArrayResponse<T>(response: any): T[] {
  if (!response) {
    console.warn('⚠️ extractArrayResponse: No response provided');
    return [];
  }

  // Handle case where response itself is an array (MSW or direct array response)
  if (Array.isArray(response)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ extractArrayResponse: Response is direct array', { length: response.length });
    }
    return response;
  }

  if (!response.data) {
    console.warn('⚠️ extractArrayResponse: No response.data', response);
    return [];
  }

  const responseData = response.data;

  // If response.data is an array, return it directly
  if (Array.isArray(responseData)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ extractArrayResponse: Direct array found', { length: responseData.length });
    }
    return responseData;
  }

  // If response.data has a 'data' property that is an array
  // This handles CategoryListResponse format: { data: [...], pagination: {...} }
  if (typeof responseData === 'object' && responseData !== null) {
    // Check for 'data' property (handles both snake_case and camelCase after transformation)
    if ('data' in responseData) {
      const data = responseData.data;

      // ✅ Defensive check: handle null/undefined data
      if (data === null || data === undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '⚠️ extractArrayResponse: responseData.data is null/undefined, returning empty array',
            {
              responseData,
            }
          );
        }
        return [];
      }

      if (Array.isArray(data)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ extractArrayResponse: Nested array found in responseData.data', {
            length: data.length,
            hasPagination: 'pagination' in responseData,
          });
        }
        return data;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ extractArrayResponse: responseData.data exists but is not an array', {
            dataType: typeof data,
            data,
          });
        }
        // Return empty array instead of failing
        return [];
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ extractArrayResponse: responseData is object but has no "data" property', {
          keys: Object.keys(responseData),
          responseData,
        });
      }
    }
  }

  // Return empty array if format is unexpected
  console.warn('❌ extractArrayResponse: Unexpected response format, returning empty array:', {
    responseData,
    type: typeof responseData,
    isArray: Array.isArray(responseData),
    hasData: responseData && 'data' in responseData,
    dataType: responseData?.data ? typeof responseData.data : 'undefined',
    dataIsArray: Array.isArray(responseData?.data),
    keys: responseData ? Object.keys(responseData) : [],
    fullResponse: response,
  });
  return [];
}

/**
 * Handles API errors consistently
 * Extracts error message from backend response
 */
/** Extract request_id from backend error response for support/debugging (Phase 5 observability) */
export function getRequestIdFromError(error: any): string | null {
  const fromBody = error?.response?.data?.request_id;
  if (typeof fromBody === 'string' && fromBody.trim()) return fromBody.trim();
  const fromHeader = error?.response?.headers?.['x-request-id'];
  if (typeof fromHeader === 'string' && fromHeader.trim()) return fromHeader.trim();
  return null;
}

export function handleApiError(error: any): never {
  const requestId = getRequestIdFromError(error);
  // Log error for debugging (include request_id for backend correlation)
  console.error('API Error:', {
    message: error.message,
    response: error.response?.data,
    status: error.response?.status,
    url: error.config?.url,
    ...(requestId ? { request_id: requestId } : {}),
  });

  // Extract error message from backend response
  if (error.response?.data) {
    // Check for error field first (backend standard format)
    if (error.response.data.error) {
      const errorMessage =
        typeof error.response.data.error === 'string'
          ? error.response.data.error
          : String(error.response.data.error);
      if (errorMessage.trim()) {
        throw new Error(errorMessage);
      }
    }
    // Check for message field as fallback
    if (error.response.data.message) {
      const errorMessage =
        typeof error.response.data.message === 'string'
          ? error.response.data.message
          : String(error.response.data.message);
      if (errorMessage.trim()) {
        throw new Error(errorMessage);
      }
    }
  }

  // Handle network errors (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('CONNECTION_TIMEOUT'); // Will be translated in client
    }
    if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
      throw new Error('NETWORK_ERROR'); // Will be translated in client
    }
    throw new Error('CONNECTION_ERROR'); // Will be translated in client
  }

  // Handle HTTP status codes
  const status = error.response?.status;
  if (status === 401 || status === 403) {
    throw new Error('UNAUTHORIZED_ACCESS'); // Will be translated in client
  }
  if (status === 404) {
    throw new Error('RESOURCE_NOT_FOUND'); // Will be translated in client
  }
  if (status === 400) {
    throw new Error('INVALID_INPUT_DATA'); // Will be translated in client
  }
  if (status === 409) {
    throw new Error('CONFLICT'); // Will be translated in client
  }
  if (status === 413) {
    throw new Error('REQUEST_ENTITY_TOO_LARGE'); // Will be translated in client
  }
  if (status === 415) {
    throw new Error('UNSUPPORTED_MEDIA_TYPE'); // Will be translated in client
  }
  if (status === 422) {
    throw new Error('INVALID_INPUT_DATA'); // Will be translated in client
  }
  if (status >= 500) {
    throw new Error('SERVER_ERROR'); // Will be translated in client
  }

  // If it's already our custom error with a message, re-throw it
  if (error.message && error.message.trim()) {
    throw error;
  }

  // Final fallback
  throw new Error('UNEXPECTED_ERROR'); // Will be translated in client
}
